import type { IntakeSpec, FurnitureTableDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { LEG_CROSS_SECTION, PANEL_THICKNESS } from '@/lib/intakeBuilderDefaults';

function getDetails(intake: IntakeSpec): FurnitureTableDetails {
  return intake.details as FurnitureTableDetails;
}

function defaultDimensions(seats: number): { length: number; width: number; height: number } {
  if (seats <= 2) return { length: 800, width: 800, height: 720 };
  if (seats <= 4) return { length: 1200, width: 800, height: 720 };
  if (seats <= 6) return { length: 1800, width: 900, height: 720 };
  if (seats <= 8) return { length: 2200, width: 1000, height: 740 };
  return { length: 2800, width: 1100, height: 740 };
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const dims = {
    length: intake.core.size?.length ?? defaultDimensions(d.numSeats).length,
    width: intake.core.size?.width ?? defaultDimensions(d.numSeats).width,
    height: intake.core.size?.height ?? defaultDimensions(d.numSeats).height,
  };
  const matKind = intake.core.primaryMaterial ?? 'hardwood';
  const topThickness =
    matKind === 'plywood' || matKind === 'mdf' ? PANEL_THICKNESS.plywood : PANEL_THICKNESS.hardwood;
  const legSection = LEG_CROSS_SECTION[d.legStyle] || 50;

  const out: Part[] = [
    {
      id: 'top',
      name: '天板',
      kind: 'panel',
      materialId: 'mat-primary',
      width: dims.length,
      depth: dims.width,
      thickness: topThickness,
      position: [0, 0, dims.height - topThickness],
      rotation: [0, 0, 0],
      tags: ['top'],
      paramRefs: [],
      cutouts: [],
    },
  ];

  if (d.legStyle === 'pedestal') {
    out.push({
      id: 'pedestal',
      name: '脚柱',
      kind: 'leg',
      materialId: 'mat-primary',
      length: dims.height - topThickness,
      crossSection: { shape: 'square', side: legSection },
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['leg'],
      paramRefs: [],
    });
  } else if (d.legStyle === 'trestle') {
    const legPos: Array<[number, number, number]> = [
      [-(dims.length / 2 - 100), 0, 0],
      [dims.length / 2 - 100, 0, 0],
    ];
    for (let i = 0; i < legPos.length; i++) {
      out.push({
        id: `trestle_${i + 1}`,
        name: `脚 ${i + 1}`,
        kind: 'leg',
        materialId: 'mat-primary',
        length: dims.height - topThickness,
        crossSection: { shape: 'square', side: legSection },
        position: legPos[i],
        rotation: [0, 0, 0],
        tags: ['leg'],
        paramRefs: [],
      });
    }
  } else {
    const legInset = 60;
    const legPositions: Array<[number, number, number]> = [
      [-(dims.length / 2 - legInset), -(dims.width / 2 - legInset), 0],
      [dims.length / 2 - legInset, -(dims.width / 2 - legInset), 0],
      [-(dims.length / 2 - legInset), dims.width / 2 - legInset, 0],
      [dims.length / 2 - legInset, dims.width / 2 - legInset, 0],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `leg_${i + 1}`,
        name: `脚 ${i + 1}`,
        kind: 'leg',
        materialId: 'mat-primary',
        length: dims.height - topThickness,
        crossSection: { shape: 'square', side: legSection },
        position: legPositions[i],
        rotation: [0, 0, 0],
        tags: ['leg'],
        paramRefs: [],
      });
    }
  }

  if (d.hasApron && d.legStyle !== 'pedestal' && d.legStyle !== 'trestle') {
    const apronH = 80;
    const apronT = 25;
    const apronZ = dims.height - topThickness - apronH;
    out.push(
      {
        id: 'apron_front', name: '幕板（前）', kind: 'rail', materialId: 'mat-primary',
        length: dims.length - 120, width: apronT, thickness: apronH,
        position: [0, -(dims.width / 2 - 60), apronZ], rotation: [0, 0, 0],
        tags: ['apron'], paramRefs: [],
      },
      {
        id: 'apron_back', name: '幕板（後）', kind: 'rail', materialId: 'mat-primary',
        length: dims.length - 120, width: apronT, thickness: apronH,
        position: [0, dims.width / 2 - 60, apronZ], rotation: [0, 0, 0],
        tags: ['apron'], paramRefs: [],
      },
      {
        id: 'apron_left', name: '幕板（左）', kind: 'rail', materialId: 'mat-primary',
        length: dims.width - 120, width: apronT, thickness: apronH,
        position: [-(dims.length / 2 - 60), 0, apronZ], rotation: [0, 0, 90],
        tags: ['apron'], paramRefs: [],
      },
      {
        id: 'apron_right', name: '幕板（右）', kind: 'rail', materialId: 'mat-primary',
        length: dims.width - 120, width: apronT, thickness: apronH,
        position: [dims.length / 2 - 60, 0, apronZ], rotation: [0, 0, 90],
        tags: ['apron'], paramRefs: [],
      },
    );
  }

  if (d.hasStretcher && d.legStyle !== 'pedestal') {
    out.push({
      id: 'stretcher', name: '貫', kind: 'rail', materialId: 'mat-primary',
      length: dims.length - 240, width: 40, thickness: 25,
      position: [0, 0, 100], rotation: [0, 0, 0],
      tags: ['stretcher'], paramRefs: [],
    });
  }

  return out;
}

export function joints(intake: IntakeSpec): Joint[] {
  const d = getDetails(intake);
  const joinery = d.joineryType;

  const dims = {
    length: intake.core.size?.length ?? defaultDimensions(d.numSeats).length,
    width: intake.core.size?.width ?? defaultDimensions(d.numSeats).width,
    height: intake.core.size?.height ?? defaultDimensions(d.numSeats).height,
  };
  const matKind = intake.core.primaryMaterial ?? 'hardwood';
  const topThickness =
    matKind === 'plywood' || matKind === 'mdf' ? PANEL_THICKNESS.plywood : PANEL_THICKNESS.hardwood;
  const legSection = LEG_CROSS_SECTION[d.legStyle] || 50;
  const legLength = dims.height - topThickness;

  const out: Joint[] = [];

  // Mortise-tenon emits real MortiseTenonJoint with explicit placement.
  // Used when the table has aprons + four corner legs.
  if (joinery === 'mortise_tenon' && d.hasApron && d.legStyle !== 'pedestal' && d.legStyle !== 'trestle') {
    // Aprons are 80mm tall × 25mm thick (see parts()). Tenon is 40 wide × 20
    // tall × 25 long, with 5mm shoulder, oriented along the rail axis.
    const tenonLength = 25;
    const apronFrontBackLen = dims.length - 120;
    const apronSideLen = dims.width - 120;
    const meetZ = legLength - 60; // 60mm down from top-of-leg
    const halfLeg = legSection / 2;

    type RailSpec = {
      railId: string;
      railLength: number;
      axis: 'x' | 'y';
      legPairs: Array<[string, 'left' | 'right']>;
    };
    // axis: rail running along x-axis (length) is 'x'; along y-axis (width) is 'y'.
    const rails: RailSpec[] = [
      { railId: 'apron_front', railLength: apronFrontBackLen, axis: 'x', legPairs: [['leg_1', 'left'], ['leg_2', 'right']] },
      { railId: 'apron_back', railLength: apronFrontBackLen, axis: 'x', legPairs: [['leg_3', 'left'], ['leg_4', 'right']] },
      { railId: 'apron_left', railLength: apronSideLen, axis: 'y', legPairs: [['leg_1', 'left'], ['leg_3', 'right']] },
      { railId: 'apron_right', railLength: apronSideLen, axis: 'y', legPairs: [['leg_2', 'left'], ['leg_4', 'right']] },
    ];

    let idx = 1;
    for (const rail of rails) {
      for (const [legId, end] of rail.legPairs) {
        const railEndLocal: [number, number, number] =
          end === 'left' ? [-rail.railLength / 2, 0, 0] : [rail.railLength / 2, 0, 0];
        // The leg local position where the rail meets it: 60mm down from top,
        // on the leg face that the rail abuts.
        const legPosLocal: [number, number, number] = [0, 0, meetZ + halfLeg];
        out.push({
          id: `mt_${idx++}`,
          fromPartId: rail.railId,
          toPartId: legId,
          type: 'mortise_tenon',
          tenonWidth: 40,
          tenonHeight: 20,
          tenonLength,
          shoulder: 5,
          haunched: false,
          clearance: 0.2,
          axis: rail.axis,
          fromPosition: railEndLocal,
          toPosition: legPosLocal,
        });
      }
    }
    return out;
  }

  // Non-mortise-tenon paths
  if (!d.hasApron && d.legStyle !== 'pedestal' && d.legStyle !== 'trestle') {
    for (let i = 0; i < 4; i++) {
      out.push(
        joinery === 'dowel'
          ? { id: `top_to_leg_${i + 1}`, fromPartId: 'top', toPartId: `leg_${i + 1}`, type: 'dowel', diameter: 8, length: 40, count: 2 }
          : { id: `top_to_leg_${i + 1}`, fromPartId: 'top', toPartId: `leg_${i + 1}`, type: 'screw', size: 'M8', length: 60, count: 1 },
      );
    }
  } else if (d.hasApron) {
    const pairs: Array<[string, string]> = [
      ['apron_front', 'leg_1'], ['apron_front', 'leg_2'],
      ['apron_back', 'leg_3'], ['apron_back', 'leg_4'],
      ['apron_left', 'leg_1'], ['apron_left', 'leg_3'],
      ['apron_right', 'leg_2'], ['apron_right', 'leg_4'],
    ];
    let idx = 1;
    for (const [a, l] of pairs) {
      out.push(
        joinery === 'dowel'
          ? { id: `joint_${idx++}`, fromPartId: a, toPartId: l, type: 'dowel', diameter: 8, length: 40, count: 2 }
          : { id: `joint_${idx++}`, fromPartId: a, toPartId: l, type: 'screw', size: 'M6', length: 50, count: 1 },
      );
    }
  }
  return out;
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const out: Feature[] = [];
  if (intake.qualityLevel === 'extreme' || intake.qualityLevel === 'manufacturing') {
    out.push({
      id: 'edge_top',
      partId: 'top',
      kind: 'edge_treatment',
      treatment: 'roundover',
      amount: 3,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }
  return out;
}
